import React, { useCallback, memo } from 'react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import type { Goal } from '../common/types';
import { useUserHasAccessToTitan } from '../services';
import { GoalsCellContent } from './goals-cell-content';
import { GoalsPopup } from './goals-popup-content/async';

const POPUP_OFFSET: [number, number] = [-8, 8];

export type GoalPickerProps = {
	issueAri: string;
	goals: Goal[];
	goalCount: number;
	isEditable: boolean;
	editMode: boolean;
	onClose: () => void;
	onGoalPicked: (goal: Goal) => void;
	onGoalUnlinked: (goalUnlinkedId: string, remainingGoals: Goal[]) => void;
	shouldRenderToParent?: boolean;
};

const GoalsPicker = ({
	issueAri,
	goals,
	goalCount,
	isEditable,
	editMode,
	onClose,
	onGoalPicked,
	onGoalUnlinked,
	shouldRenderToParent = false,
}: GoalPickerProps) => {
	const { hasPermission, loading, hasErrorOccurred, retry } = useUserHasAccessToTitan();

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<Box
				role="button"
				xcss={triggerStyles}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...triggerProps}
			>
				<GoalsCellContent goals={goals} goalCount={goalCount} isEditable={isEditable} />
			</Box>
		),

		[goalCount, goals, isEditable],
	);

	const content = useCallback(
		() => (
			<GoalsPopup
				issueAri={issueAri}
				hasPermission={hasPermission}
				hasErrorOccurred={hasErrorOccurred}
				retryPermissions={retry}
				isLoadingPermissions={loading}
				goalCount={goalCount}
				onClose={onClose}
				onGoalPicked={onGoalPicked}
				onGoalUnlinked={onGoalUnlinked}
			/>
		),
		[
			goalCount,
			hasPermission,
			issueAri,
			onClose,
			onGoalPicked,
			loading,
			onGoalUnlinked,
			hasErrorOccurred,
			retry,
		],
	);

	return (
		<Flex xcss={goalPickerStyles} alignItems="center" direction="column">
			<Popup
				isOpen={editMode}
				content={content}
				trigger={popupTrigger}
				offset={POPUP_OFFSET}
				placement="bottom-start"
				shouldRenderToParent={shouldRenderToParent}
			/>
		</Flex>
	);
};

export default memo(GoalsPicker);

const triggerStyles = xcss({
	display: 'flex',
	height: '100%',
	width: '100%',
	flexGrow: 1,
	flexDirection: 'column',
	alignItems: 'center',
});

const goalPickerStyles = xcss({
	minHeight: '100%',
	width: '100%',
});
